import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { Delta as TypeDelta, Sources } from 'quill'
import { debounce } from 'lodash'
import ReactQuill from 'react-quill'
import { theme } from 'antd'
import 'react-quill/dist/quill.snow.css'

import { toolbarOptions } from './toolbarOptions'
import { EditorProps } from '../Course/types'
import { generatePreviewImage } from './generatePreviewImage'
import { CourseContext } from '../Course/CourseContext'

const { useToken } = theme

export const Editor: React.FC<EditorProps> = ({ course, displayedPage }) => {
  const { token } = useToken()

  const { updateCourse } = useContext(CourseContext)
  const [ stateEditorContent, setStateEditorContent ] = useState(course.pages[displayedPage].content)

  /* 
    if the course has questions AND the displayed page is the last page, then the editor is read only
    (this means that the questions are displayed and they can be edited in the questions viewport tab)
  */
  const isReadOnly = course.questions?.length && displayedPage + 1 === course.pages.length ? true : false

  useEffect(() => {
    setStateEditorContent(course.pages[displayedPage].content)
  }, [ displayedPage ])

  const setContextWithPreviewPictureAndEditorContent = debounce(async (editorContent) => {
    const previewImage = await generatePreviewImage()
    const newPages = [ ...course.pages ]

    newPages[displayedPage] = {
      ...newPages[displayedPage],
      content: {
        ops: editorContent.ops
      },
      previewPicture: previewImage
    }

    const newCourseObject = {
      ...course,
      pages: newPages,
    }

    updateCourse(newCourseObject)
  }, 5000, { trailing: true })

  const handleChange = async (value: string, delta: TypeDelta, source: Sources, editor: ReactQuill.UnprivilegedEditor) => {
    const currentEditorContent = editor.getContents()

    if (JSON.stringify(currentEditorContent) === JSON.stringify(stateEditorContent)) {
      return // prevent updating context on initial render
    }

    setStateEditorContent({ ops: currentEditorContent.ops || []})
    setContextWithPreviewPictureAndEditorContent(currentEditorContent)
  }

  return (
    <Container
      backgroundColor={token.colorBgContainer}
      color={token.colorTextSecondary}
    >
      <QuillEditor
        theme="snow"
        value={stateEditorContent as unknown as TypeDelta}
        onChange={handleChange}
        modules={{ toolbar: toolbarOptions }}
        readOnly={isReadOnly}
      />
    </Container>
  )
}

export const QuillEditor = styled(ReactQuill)`
  .ql-editor {
    height: 50vh;
  }

  .ql-editor ul[data-checked=false] > li::before {
    font-size: 1.5em !important;
  }

  .ql-editor ul[data-checked=true] > li::before {
    font-size: 1.5em !important;
  }

  .ql-toolbar {
    display: ${props => props.readOnly ? 'none' : 'block'};
  }
`

const Container = styled.div<{
  backgroundColor: string
  color: string
}>`
  background-color: ${props => props.backgroundColor};
  color: ${props => props.color};
  margin: 1em;
`
